/**
 * Plant-Aware API Utility
 * Automatically includes plant_id in all API requests
 * Uses active plant from localStorage
 */

import Axios from "axios";

/**
 * Get active plant from localStorage
 * @returns {Object|null} Active plant object or null
 */
const getActivePlant = () => {
  try {
    const plantData = localStorage.getItem('active-plant');
    return plantData ? JSON.parse(plantData) : null;
  } catch (error) {
    console.error('Error reading active plant from localStorage:', error);
    return null;
  }
};

/**
 * Get authorization token from localStorage
 * @returns {string|null} Token or null
 */
const getToken = () => {
  return localStorage.getItem('Token');
};

/**
 * Plant-aware GET request
 * Automatically includes plant_id in query parameters
 *
 * @param {string} url - API endpoint URL
 * @param {Object} params - Additional query parameters
 * @param {Object} config - Additional axios config
 * @returns {Promise} Axios promise
 */
export const plantApiGet = async (url, params = {}, config = {}) => {
  const token = getToken();
  const activePlant = getActivePlant();

  const headers = {
    Authorization: `Bearer ${token}`,
    ...config.headers
  };

  const queryParams = {
    ...params,
    ...(activePlant?.id && { plant_id: activePlant.id })
  };

  return Axios.get(url, {
    ...config,
    params: queryParams,
    headers
  });
};

/**
 * Plant-aware POST request
 * Automatically includes plant_id in request body
 *
 * @param {string} url - API endpoint URL
 * @param {Object} data - Request body data
 * @param {Object} config - Additional axios config
 * @returns {Promise} Axios promise
 */
export const plantApiPost = async (url, data = {}, config = {}) => {
  const token = getToken();
  const activePlant = getActivePlant();

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...config.headers
  };

  const bodyData = {
    ...data,
    ...(activePlant?.id && { plant_id: activePlant.id })
  };

  return Axios.post(url, bodyData, {
    ...config,
    headers
  });
};

/**
 * Plant-aware PUT request
 * Automatically includes plant_id in request body
 *
 * @param {string} url - API endpoint URL
 * @param {Object} data - Request body data
 * @param {Object} config - Additional axios config
 * @returns {Promise} Axios promise
 */
export const plantApiPut = async (url, data = {}, config = {}) => {
  const token = getToken();
  const activePlant = getActivePlant();

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...config.headers
  };

  const bodyData = {
    ...data,
    ...(activePlant?.id && { plant_id: activePlant.id })
  };

  return Axios.put(url, bodyData, {
    ...config,
    headers
  });
};

/**
 * Plant-aware DELETE request
 * Automatically includes plant_id in query parameters
 *
 * @param {string} url - API endpoint URL
 * @param {Object} params - Additional query parameters
 * @param {Object} config - Additional axios config
 * @returns {Promise} Axios promise
 */
export const plantApiDelete = async (url, params = {}, config = {}) => {
  const token = getToken();
  const activePlant = getActivePlant();

  const headers = {
    Authorization: `Bearer ${token}`,
    ...config.headers
  };

  const queryParams = {
    ...params,
    ...(activePlant?.id && { plant_id: activePlant.id })
  };

  return Axios.delete(url, {
    ...config,
    params: queryParams,
    headers
  });
};

/**
 * Plant-aware FormData POST request
 * Automatically includes plant_id in FormData
 *
 * @param {string} url - API endpoint URL
 * @param {FormData} formData - FormData object
 * @param {Object} config - Additional axios config
 * @returns {Promise} Axios promise
 */
export const plantApiPostFormData = async (url, formData, config = {}) => {
  const token = getToken();
  const activePlant = getActivePlant();

  // Add plant_id to FormData if active plant exists
  if (activePlant?.id && formData instanceof FormData) {
    formData.append('plant_id', activePlant.id);
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    ...config.headers
    // Don't set Content-Type for FormData - browser will set it with boundary
  };

  return Axios.post(url, formData, {
    ...config,
    headers
  });
};

/**
 * Get current plant information
 * Useful for displaying plant context in components
 *
 * @returns {Object} Object with plant info and helper methods
 */
export const getCurrentPlant = () => {
  const activePlant = getActivePlant();

  return {
    plant: activePlant,
    plantId: activePlant?.id,
    plantCode: activePlant?.code,
    plantName: activePlant?.name,
    hasPlant: !!activePlant,
    plantInfo: activePlant ? {
      id: activePlant.id,
      code: activePlant.code,
      name: activePlant.name,
      city: activePlant.city,
      state: activePlant.state,
      type: activePlant.type
    } : null
  };
};

/**
 * Check if plant_id should be included in request
 * Some endpoints (like login, public APIs) don't need plant_id
 *
 * @param {string} url - API endpoint URL
 * @returns {boolean} True if plant_id should be included
 */
export const shouldIncludePlantId = (url) => {
  const excludedPatterns = [
    '/api/token/',
    '/api/refresh/',
    '/api/plants/all',
    '/api/plants/user',
    '/login',
    '/logout'
  ];

  return !excludedPatterns.some(pattern => url.includes(pattern));
};

/**
 * Wrapper for existing API functions to make them plant-aware
 * Can be used to gradually migrate existing API calls
 *
 * @param {Function} apiFunction - Existing API function
 * @returns {Function} Plant-aware version of the function
 */
export const makePlantAware = (apiFunction) => {
  return async (...args) => {
    const activePlant = getActivePlant();

    // If there are params/data arguments, inject plant_id
    const modifiedArgs = args.map((arg, index) => {
      if (typeof arg === 'object' && arg !== null && !Array.isArray(arg)) {
        return {
          ...arg,
          ...(activePlant?.id && { plant_id: activePlant.id })
        };
      }
      return arg;
    });

    return apiFunction(...modifiedArgs);
  };
};

// Export default object with all methods
const plantApi = {
  get: plantApiGet,
  post: plantApiPost,
  put: plantApiPut,
  delete: plantApiDelete,
  postFormData: plantApiPostFormData,
  getCurrentPlant,
  shouldIncludePlantId,
  makePlantAware
};

export default plantApi;
